
import React, { useState, useEffect } from 'react';
import { ChatRequest, User, RequestStatus, UserRole } from '../types.ts';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';

const MeetingRoom = ({ user, request, onLeave }: { user: User, request: ChatRequest, onLeave: () => void }) => {
  const [timeRemaining, setTimeRemaining] = useState(1800);
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [persistenceEnabled, setPersistenceEnabled] = useState(false);
  const [persistenceInterval, setPersistenceInterval] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeCopilotTab, setActiveCopilotTab] = useState<'pivot' | 'value' | 'recap'>('pivot');

  useEffect(() => {
    const timer = setInterval(() => setTimeRemaining(p => p > 0 ? p - 1 : 0), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmitFeedback = async () => {
    if (rating === 0) return alert("Please rate the synergy level.");
    setIsSubmitting(true);
    const updated = { ...request, status: RequestStatus.COMPLETED, updatedAt: Date.now() };
    
    if (user.role === UserRole.STUDENT) {
      updated.studentFeedback = { rating, comment, timestamp: Date.now() };
      if (persistenceEnabled) {
        updated.followUpEnabled = true;
        updated.followUpFrequency = persistenceInterval;
        const draft = await aiService.generatePostCallFollowUp(dbService.getStudentProfile(user.id), dbService.getProfessionalProfile(request.professionalId), "Discussed career journey and TMT M&A.");
        updated.followUpDraft = draft.followUpDraft;
      }
    } else {
      updated.proFeedback = { rating, comment, timestamp: Date.now() };
    }

    dbService.saveRequest(updated);
    setIsSubmitting(false);
    onLeave();
  };

  return (
    <div className="fixed inset-0 bg-slate-950 text-white z-[100] flex flex-col font-sans overflow-hidden">
      <header className="h-20 px-8 border-b border-white/5 flex items-center justify-between bg-slate-900/50 backdrop-blur-xl shrink-0">
        <div className="flex items-center gap-6">
          <div className="bg-indigo-600 p-3 rounded-2xl shadow-lg shadow-indigo-600/20"><i className="fas fa-mug-hot"></i></div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-400">Live Synchronization</p>
            <p className="text-sm font-black uppercase tracking-widest">{Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')} REmaining</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
           <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">Encrypted Protocol</span>
           </div>
           <button onClick={() => setShowFeedback(true)} className="px-8 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl transition-all active:scale-95">Terminate Session</button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Video Grid */}
        <main className="flex-1 p-12 grid grid-cols-1 md:grid-cols-2 gap-12 bg-slate-900/20 relative">
           <div className="bg-slate-800/40 rounded-[4rem] border border-white/5 flex flex-col items-center justify-center relative group overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-32 h-32 bg-slate-700/50 rounded-full flex items-center justify-center text-5xl font-black text-slate-500">P</div>
              <p className="mt-6 text-sm font-black uppercase tracking-[0.4em] text-slate-600 group-hover:text-white transition-colors">Partner Feed</p>
              <div className="absolute bottom-10 left-10 opacity-0 group-hover:opacity-100 transition-all flex items-center gap-3">
                 <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                 <span className="text-xs font-black uppercase tracking-widest">Low Latency Active</span>
              </div>
           </div>
           <div className="bg-slate-800/40 rounded-[4rem] border border-indigo-500/20 flex flex-col items-center justify-center relative group overflow-hidden shadow-2xl">
              <div className="w-24 h-24 bg-slate-700/50 rounded-full flex items-center justify-center text-4xl font-black text-slate-500">Y</div>
              <p className="mt-6 text-sm font-black uppercase tracking-[0.4em] text-slate-600 group-hover:text-white transition-colors">Your Feed</p>
              <div className="absolute bottom-10 right-10 flex gap-4">
                 <button className="w-12 h-12 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors border border-white/5"><i className="fas fa-microphone"></i></button>
                 <button className="w-12 h-12 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors border border-white/5"><i className="fas fa-video"></i></button>
              </div>
           </div>
        </main>

        {/* RELATIONAL COPILOT SIDEBAR - UNIQUE DIFFERENTIATOR */}
        <aside className="w-96 bg-slate-900 border-l border-white/5 flex flex-col shadow-4xl relative overflow-hidden hidden lg:flex shrink-0">
           <div className="absolute top-0 right-0 p-8 opacity-5 text-9xl text-indigo-500 pointer-events-none font-black"><i className="fas fa-brain"></i></div>
           
           <div className="p-8 border-b border-white/5 shrink-0">
              <h3 className="text-xl font-black tracking-tighter mb-4 flex items-center gap-3 text-indigo-400">
                <i className="fas fa-robot text-sm"></i> Sage Live Copilot
              </h3>
              <div className="flex p-1 bg-black/40 rounded-xl border border-white/5">
                 {['pivot', 'value', 'recap'].map((t) => (
                   <button 
                    key={t}
                    onClick={() => setActiveCopilotTab(t as any)}
                    className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeCopilotTab === t ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                   >
                     {t}
                   </button>
                 ))}
              </div>
           </div>

           <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-hide">
              {activeCopilotTab === 'pivot' && (
                <div className="space-y-6 animate-in slide-in-from-right-4">
                   <div className="space-y-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Suggested Strategic Pivots</p>
                      <div className="p-5 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-all cursor-pointer group">
                         <p className="text-xs font-bold leading-relaxed mb-3 text-indigo-200">"Since you mentioned their shift from J.P. Morgan, ask how that redefined their risk tolerance."</p>
                         <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 group-hover:text-indigo-400">Context: Pivot logic</span>
                      </div>
                      <div className="p-5 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-all cursor-pointer group">
                         <p className="text-xs font-bold leading-relaxed mb-3 text-indigo-200">"Ask about the 'unwritten rules' of their current division's culture."</p>
                         <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 group-hover:text-indigo-400">Context: Institutional Memory</span>
                      </div>
                   </div>
                </div>
              )}

              {activeCopilotTab === 'value' && (
                <div className="space-y-6 animate-in slide-in-from-right-4">
                   <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Reciprocity Alerts</p>
                   <div className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-3xl">
                      <p className="text-xs font-bold text-emerald-400 leading-relaxed mb-4">"Liam is currently focusing on Low-Latency C++. Mention your recent Order Book project to validate technical depth."</p>
                      <div className="flex items-center gap-2">
                        <i className="fas fa-hand-holding-heart text-[10px] text-emerald-500"></i>
                        <span className="text-[9px] font-black uppercase text-emerald-500 tracking-widest">Giving Opportunity</span>
                      </div>
                   </div>
                </div>
              )}

              {activeCopilotTab === 'recap' && (
                <div className="space-y-6 animate-in slide-in-from-right-4 opacity-40">
                   <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Live Memory Ledger</p>
                   <p className="text-[10px] font-bold text-slate-400 italic">Syncing session nodes... Artifact generation will trigger at session termination.</p>
                </div>
              )}
           </div>

           <div className="p-8 mt-auto shrink-0 bg-black/20 border-t border-white/5">
              <div className="flex items-center gap-4 p-4 bg-indigo-600/10 rounded-2xl border border-indigo-600/20">
                 <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
                 <p className="text-[9px] font-black uppercase tracking-widest text-indigo-300">Sage Listening for Pivots...</p>
              </div>
           </div>
        </aside>
      </div>

      {showFeedback && (
        <div className="fixed inset-0 z-[200] bg-blue-950/90 backdrop-blur-2xl flex items-center justify-center p-6 animate-in fade-in">
          <div className="bg-white w-full max-w-lg rounded-[3.5rem] p-12 text-slate-900 shadow-4xl overflow-y-auto max-h-[90vh]">
             <h2 className="text-4xl font-black tracking-tighter mb-2 text-blue-950">Memory Ledger.</h2>
             <p className="text-slate-500 font-medium mb-10 leading-relaxed">Seal this connection into your relational ecosystem.</p>
             
             <div className="space-y-8">
                <div className="flex justify-center gap-4">
                   {[1, 2, 3, 4, 5].map(s => <button key={s} onClick={() => setRating(s)} className={`text-4xl transition-all ${rating >= s ? 'text-amber-500 scale-110' : 'text-slate-100'}`}><i className="fas fa-star"></i></button>)}
                </div>

                {user.role === UserRole.STUDENT && (
                  <div className="space-y-6">
                    <label className="p-6 bg-blue-50 rounded-[2.5rem] flex items-center justify-between cursor-pointer border border-blue-100">
                       <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-900 text-white rounded-xl flex items-center justify-center"><i className="fas fa-infinity"></i></div>
                          <div>
                             <p className="font-black text-[10px] text-blue-950 uppercase tracking-widest leading-none mb-1">Network Persistence</p>
                             <p className="text-[10px] font-black text-blue-600">AI-Driven Periodic Handshake</p>
                          </div>
                       </div>
                       <input type="checkbox" checked={persistenceEnabled} onChange={() => setPersistenceEnabled(!persistenceEnabled)} className="w-6 h-6 accent-blue-900" />
                    </label>

                    {persistenceEnabled && (
                      <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded-2xl">
                         {['1week', '2weeks', '1month'].map(interval => (
                           <button key={interval} onClick={() => setPersistenceInterval(interval as any)} className={`py-3 rounded-xl text-[9px] font-black uppercase transition-all ${persistenceInterval === interval ? 'bg-blue-950 text-white shadow-xl' : 'text-slate-400 hover:text-blue-900'}`}>
                             {interval.replace('week', ' Wk').replace('month', ' Mo')}
                           </button>
                         ))}
                      </div>
                    )}
                  </div>
                )}

                <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="What was the most impactful exchange?" className="w-full p-6 bg-slate-50 border-none rounded-[2rem] h-28 outline-none font-medium text-sm focus:ring-2 ring-blue-500/10" />

                <button onClick={handleSubmitFeedback} disabled={isSubmitting} className="w-full py-5 bg-blue-950 text-white rounded-[2rem] font-black text-lg shadow-4xl hover:bg-blue-900 transition-all active:scale-95 disabled:opacity-50">
                   {isSubmitting ? 'Finalizing Ledger...' : 'Commit to Network'}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingRoom;
